import React, { useState } from 'react';
import { Incrementer } from '../../primitives/Incrementer';
import { InfoTooltip } from '../../primitives/InfoTooltip';
import {
  TEST_CAMPAIGN_CHARACTERS,
  TEST_PRESET_CATEGORY_OPTIONS,
  DEFAULT_TEST_PRESET_CATEGORIES,
  TEST_PRESET_CATEGORY_FEATURE_GATES,
} from './constants';

const EMAIL_CAP = 25;
const EMAIL_LABEL_WIDTH = 86;
const EMAIL_CHIP_MARGIN_LEFT = EMAIL_LABEL_WIDTH + 10;

export const TableStep = ({ form, update, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};

  const [emailDraft, setEmailDraft] = useState('');
  const [emailFocus, setEmailFocus] = useState(false);

  const invitedEmails = form.invitedEmails || [];
  const trimmedDraft = emailDraft.trim();
  const draftIsDuplicate = invitedEmails.some(
    (email) => email.toLowerCase() === trimmedDraft.toLowerCase(),
  );
  const atEmailCap = invitedEmails.length >= EMAIL_CAP;
  const canAddEmail = trimmedDraft.length > 0 && !draftIsDuplicate && !atEmailCap;

  const addEmailInvite = () => {
    if (!canAddEmail) return;
    update({ invitedEmails: [...invitedEmails, trimmedDraft] });
    setEmailDraft('');
  };
  const removeEmailInvite = (email) => {
    update({ invitedEmails: invitedEmails.filter((candidate) => candidate !== email) });
  };

  const displayDummyCount = Math.max(0, Math.min(4, Math.floor(Number(form.dummyCount) || 0)));
  const dummyHelper = displayDummyCount === 0
    ? 'none'
    : `${displayDummyCount} test character${displayDummyCount === 1 ? '' : 's'}`;
  const visibleTestCharacters = TEST_CAMPAIGN_CHARACTERS.slice(0, displayDummyCount);

  const getRawPresetCategories = () => ({
    ...DEFAULT_TEST_PRESET_CATEGORIES,
    ...(form.testPresetCategories || {}),
    ...(form.testCharacterPresetCategories || {}),
  });
  const isPresetCategoryFeatureEnabled = (categoryKey) => {
    const requiredFeatures = TEST_PRESET_CATEGORY_FEATURE_GATES[categoryKey] || [];
    return requiredFeatures.every((featureKey) => form.features?.[featureKey] !== false);
  };
  const getEffectivePresetCategories = () => {
    const raw = getRawPresetCategories();
    return Object.fromEntries(
      TEST_PRESET_CATEGORY_OPTIONS.map((option) => [
        option.key,
        isPresetCategoryFeatureEnabled(option.key) && raw[option.key] !== false,
      ]),
    );
  };
  const updatePresetCategory = (categoryKey, checked) => {
    const current = getRawPresetCategories();
    update({
      testPresetCategories: { ...current, [categoryKey]: checked },
      testCharacterPresetCategories: { ...current, [categoryKey]: checked },
    });
  };
  const presetCategories = getEffectivePresetCategories();

  return (
    <div>
      <h3 style={{
        margin: '0 0 16px',
        fontFamily: t.headingFamily,
        fontSize: '1.4rem',
        fontWeight: 600,
        color: c.accentLight,
        letterSpacing: '0.01em',
        lineHeight: 1.2,
      }}>Who plays?</h3>

      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}>
          <div style={{
            fontSize: '0.92rem',
            color: c.textSecondary,
            fontFamily: t.fontFamily,
          }}>Invite players</div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 8,
            fontFamily: t.fontFamily,
            fontSize: '0.82rem',
            color: c.textMuted,
          }}>
            <span>Code:</span>
            <span
              data-testid="forge-invite-code"
              style={{
                fontFamily: t.monoFamily || 'ui-monospace, monospace',
                fontSize: '0.86rem',
                letterSpacing: '0.14em',
                color: c.textPrimary,
                fontWeight: 600,
              }}
            >Generated on finish</span>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 8,
        }}>
          <div style={{
            flex: `0 0 ${EMAIL_LABEL_WIDTH}px`,
            fontFamily: t.fontFamily,
            fontSize: '0.85rem',
            color: c.textSecondary,
            fontWeight: 500,
          }}>Email</div>
          <input
            type="email"
            value={emailDraft}
            onChange={(ev) => setEmailDraft(ev.target.value)}
            onFocus={() => setEmailFocus(true)}
            onBlur={() => setEmailFocus(false)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                addEmailInvite();
              }
            }}
            placeholder="player@example.com"
            aria-label="Email address"
            disabled={atEmailCap}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: c.bgInput,
              border: `1px solid ${emailFocus ? c.accent : c.borderLight}`,
              borderRadius: 6,
              color: c.textPrimary,
              fontSize: '0.88rem',
              fontFamily: t.fontFamily,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 150ms ease',
              opacity: atEmailCap ? 0.5 : 1,
              cursor: atEmailCap ? 'not-allowed' : 'text',
            }}
          />
          <button
            type="button"
            onClick={addEmailInvite}
            disabled={!canAddEmail}
            style={{
              padding: '8px 16px',
              background: canAddEmail ? c.accent : 'transparent',
              color: canAddEmail ? c.textOnAccent : c.textMuted,
              border: canAddEmail ? 'none' : `1px solid ${c.borderLight}`,
              borderRadius: 6,
              cursor: canAddEmail ? 'pointer' : 'not-allowed',
              fontFamily: t.fontFamily,
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'all 120ms ease',
              flex: '0 0 auto',
            }}
          >Add</button>
        </div>

        {atEmailCap && (
          <div style={{
            marginLeft: EMAIL_CHIP_MARGIN_LEFT,
            fontSize: '0.76rem',
            color: c.textMuted,
            fontStyle: 'italic',
            fontFamily: t.fontFamily,
            marginBottom: invitedEmails.length > 0 ? 8 : 0,
          }}>Email invite cap reached ({EMAIL_CAP}).</div>
        )}

        {invitedEmails.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginLeft: EMAIL_CHIP_MARGIN_LEFT,
            marginBottom: 4,
          }}>
            {invitedEmails.map((email) => (
              <span
                key={email}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px 4px 12px',
                  background: `${c.accent}14`,
                  border: `1px solid ${c.accent}66`,
                  borderRadius: 999,
                  fontFamily: t.fontFamily,
                  fontSize: '0.82rem',
                  color: c.accentLight,
                }}
              >
                {email}
                <button
                  type="button"
                  onClick={() => removeEmailInvite(email)}
                  aria-label={`Remove ${email}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: c.accentLight,
                    fontSize: '0.95rem',
                    lineHeight: 1,
                    cursor: 'pointer',
                    opacity: 0.7,
                  }}
                >x</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 6,
        }}>
          <div style={{
            fontSize: '0.92rem',
            color: c.textSecondary,
            fontFamily: t.fontFamily,
          }}>Test characters</div>
          <InfoTooltip
            theme={theme}
            text="Local-only test characters for previewing this campaign in the sandbox."
          />
        </div>
        <Incrementer
          theme={theme}
          value={displayDummyCount}
          min={0}
          max={4}
          onChange={(next) => update({ dummyCount: next })}
          helperText={dummyHelper}
          ariaLabel="Test character count"
        />
        {visibleTestCharacters.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div
              style={{
                padding: '10px 12px',
                border: `1px solid ${c.borderLight}`,
                borderRadius: 6,
                background: 'rgba(255,255,255,0.025)',
              }}
            >
              <div style={{
                marginBottom: 8,
                fontFamily: t.fontFamily,
                color: c.textPrimary,
                fontSize: '0.86rem',
                fontWeight: 600,
              }}>Seed test characters with</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                gap: 6,
              }}>
                {TEST_PRESET_CATEGORY_OPTIONS.map((option) => {
                  const categoryEnabled = isPresetCategoryFeatureEnabled(option.key);
                  return (
                    <label
                      key={option.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                        color: categoryEnabled ? c.textSecondary : c.textMuted,
                        fontFamily: t.fontFamily,
                        fontSize: '0.78rem',
                        lineHeight: 1.25,
                        opacity: categoryEnabled ? 1 : 0.6,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={presetCategories[option.key] !== false}
                        disabled={!categoryEnabled}
                        onChange={(ev) => updatePresetCategory(option.key, ev.target.checked)}
                        aria-label={option.label}
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
              <div style={{
                marginTop: 8,
                color: c.textMuted,
                fontFamily: t.fontFamily,
                fontSize: '0.75rem',
                lineHeight: 1.35,
              }}>
                Presets are copied to local test characters. Real player characters stay separate.
              </div>
            </div>
            {visibleTestCharacters.map((character) => (
              <div
                key={character.presetKey}
                style={{
                  padding: '10px 12px',
                  border: `1px solid ${c.borderLight}`,
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.025)',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  fontFamily: t.fontFamily,
                }}>
                  <strong style={{ color: c.textPrimary, fontSize: '0.9rem' }}>{character.name}</strong>
                  <span style={{ color: c.textMuted, fontSize: '0.76rem' }}>
                    {character.race} {character.charClass}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
